// frontend/src/pages/ArchivedPage.jsx
/**
 * Archived tasks. A manager tips a completed task into the archive from the
 * Workflow board; it lands here — still fully readable (click through to its
 * detail page for tasks, time, files and audit trail) and restorable.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getArchivedTasks, restoreTask } from '../api';
import TimelineDrawer from '../components/TimelineDrawer';

const isManager = (user) => user?.role === 'MANAGER' || user?.role === 'ADMIN';

export default function ArchivedPage({ user }) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringId, setRestoringId] = useState(null);
  const [history, setHistory] = useState(null); // { id, label }

  const load = () => {
    setLoading(true);
    getArchivedTasks()
      .then((data) => setTasks(Array.isArray(data) ? data : (data.results || [])))
      .catch(() => setTasks([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleRestore = async (task) => {
    if (!window.confirm(`Restore "${task.name || task.client_name}" back to the active Tasks and Workflow board?`)) {
      return;
    }
    setRestoringId(task.id);
    try {
      await restoreTask(task.id);
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
    } catch (e) {
      alert('Failed to restore task.');
    } finally {
      setRestoringId(null);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 4 }}>📦 Archived</h1>
      <div style={{ color: '#6b7280', marginBottom: 20, fontSize: 14 }}>
        Completed tasks moved out of the active board. They stay fully readable, and a manager can restore any of them.
      </div>

      {loading && <div style={{ color: '#6b7280' }}>Loading…</div>}

      {!loading && tasks.length === 0 && (
        <div style={{ color: '#6b7280', padding: 40, textAlign: 'center' }}>
          Nothing archived yet.
        </div>
      )}

      {!loading && tasks.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={th}>Task</th>
                <th style={th}>Client</th>
                <th style={th}>Year</th>
                <th style={th}>Archived</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => (
                <tr key={t.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={td}>
                    <button
                      onClick={() => navigate(`/tasks/${t.id}`)}
                      style={linkBtn}
                    >
                      {t.name || t.task_type_name || `Task ${t.id}`}
                    </button>
                  </td>
                  <td style={td}>{t.client_name || '—'}</td>
                  <td style={td}>{t.fiscal_year || '—'}</td>
                  <td style={td}>{fmtDate(t.archived_at)}</td>
                  <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => setHistory({ id: t.id, label: t.name || t.client_name })}
                      style={ghostBtn}
                    >History</button>
                    {isManager(user) && (
                      <button
                        onClick={() => handleRestore(t)}
                        disabled={restoringId === t.id}
                        style={restoreBtn}
                      >
                        {restoringId === t.id ? 'Restoring…' : 'Restore'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TimelineDrawer
        open={!!history}
        onClose={() => setHistory(null)}
        recordType="task"
        recordId={history?.id}
        title={history ? `History · ${history.label || 'Task'}` : 'History'}
      />
    </div>
  );
}

const th = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '10px 12px', fontSize: 13 };
const linkBtn = { background: 'transparent', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0, textAlign: 'left' };
const ghostBtn = { background: 'transparent', border: '1px solid #d1d5db', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12, color: '#374151', marginRight: 8 };
const restoreBtn = { background: '#059669', border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12, color: '#fff', fontWeight: 600 };
